/**
 * Liste des préévaluations Manager — US-10 / Lot 8.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  preevalApi,
  domainsApi,
  ApiRequestError,
  type PreevalSummaryDto,
  type DomainDto,
} from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";

export function PreevalListPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<PreevalSummaryDto[]>([]);
  const [domains, setDomains] = useState<DomainDto[]>([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [domain, setDomain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await domainsApi.list();
        if (!cancelled) setDomains(d.domains.filter((x) => x.active));
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await preevalApi.list({
          year,
          domain: domain || undefined,
        });
        if (!cancelled) setItems(res.preevals);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [year, domain]);

  return (
    <div className="sm-page">
      <PageHeader
        title={t("preevals")}
        subtitle={t("preevals_sub")}
        actions={
          <div className="sm-form-actions">
            <Link to="/preevals/gaps" className="sm-btn sm-btn-secondary">
              {t("preeval_gaps")}
            </Link>
            <Link to="/preevals/new" className="sm-btn sm-btn-primary">
              {t("new_preeval")}
            </Link>
          </div>
        }
      />

      <div className="sm-card sm-card-pad" style={{ marginBottom: 12, display: "flex", gap: 12, flexWrap: "wrap" }}>
        <label className="sm-muted">
          {t("year")}{" "}
          <input
            type="number"
            className="sm-input"
            min={2020}
            max={2100}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{ width: 100, marginLeft: 6 }}
          />
        </label>
        <label className="sm-muted">
          {t("domain")}{" "}
          <select
            className="sm-input"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            style={{ marginLeft: 6, minWidth: 160 }}
          >
            <option value="">{t("all")}</option>
            {domains.map((d) => (
              <option key={d.id} value={d.code}>
                {d.code}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="sm-card">
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>{t("full_name")}</th>
                <th>{t("domain")}</th>
                <th>{t("erp")}</th>
                <th>{t("year")}</th>
                <th>{t("preeval_progress_col")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="sm-muted" style={{ textAlign: "center", padding: 24 }}>
                    {t("loading")}
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="sm-muted" style={{ textAlign: "center", padding: 24 }}>
                    {t("no_data")}
                  </td>
                </tr>
              ) : (
                items.map((p) => {
                  const pct =
                    p.lineCount > 0 ? Math.round((p.scoredCount / p.lineCount) * 100) : 0;
                  const fillLabel =
                    p.scoredCount === 0
                      ? t("preeval_fill_none")
                      : p.scoredCount >= p.lineCount
                        ? t("preeval_fill_full")
                        : t("preeval_fill_partial");
                  const fillClass =
                    p.scoredCount === 0
                      ? "sm-badge-pill sm-badge-gray"
                      : p.scoredCount >= p.lineCount
                        ? "sm-badge-pill sm-badge-green"
                        : "sm-badge-pill sm-badge-yellow";
                  return (
                    <tr key={p.id}>
                      <td>
                        {p.expertName}
                        <div className="sm-muted" style={{ fontSize: 12 }}>
                          {p.expertInternalId}
                        </div>
                      </td>
                      <td>{p.domain}</td>
                      <td>{p.applicationLabel}</td>
                      <td>{p.year}</td>
                      <td>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span className={fillClass}>{fillLabel}</span>
                          <span className="sm-muted" style={{ fontSize: 12 }}>
                            {p.scoredCount}/{p.lineCount} ({pct}%)
                          </span>
                        </div>
                      </td>
                      <td>
                        <Link to={`/preevals/${p.id}`} className="sm-btn sm-btn-secondary sm-btn-sm">
                          {t("preeval_continue")}
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
